/**
 * Marketing Copy
 *
 * Centralized localized copy for the redesigned public marketing pages.
 *
 * Primary responsibilities:
 * - Keep public positioning consistent across marketing routes
 * - Provide translated copy for popular languages with English fallback
 *
 * Dependencies:
 * - None
 */

type MarketingLocale = 'en' | 'es' | 'fr' | 'de' | 'pt' | 'ar' | 'ja' | 'ko' | 'zh'

const englishMarketingCopy = {
  common: {
    home: 'Home',
    features: 'Features',
    docs: 'Docs',
    importAi: 'Import',
    aiReadiness: 'AI Readiness',
    agents: 'Business',
    madeInCanada: 'Made in Canada',
    support: 'Support',
    privacy: 'Privacy',
    terms: 'Terms',
    startFree: 'Start free',
    logIn: 'Log in',
    exploreFeatures: 'Explore features',
    seeHowLosiWorks: 'See how Losi works',
    seePlans: 'See plans',
    seeLosiNexus: 'Explore Nexus',
    checkAiReadiness: 'Check AI readiness',
    explorePlatform: 'Explore the platform',
    openApp: 'Get started',
    cancel: 'Cancel',
    sendMessage: 'Send message',
    sending: 'Sending',
    supportSuccess: 'Support request submitted successfully. We will get back to you soon.',
    supportFillAll: 'Please fill in all fields',
    supportEmailInvalid: 'Please enter a valid email address',
    supportSubmitError: 'Failed to submit support request'
  },
  footer: {
    platformTitle: 'Platform',
    companyTitle: 'Company',
    legalTitle: 'Legal',
    studioLabel: 'Marshmallow Studio',
    // Promise + Crew as below-fold brand seal (not H1).
    tagline:
      'Tuesday knows what Monday did. Your work stays, builds, and belongs to you.',
    ctaTitle: 'Safe to go all in.',
    ctaDescription:
      'Start in chat, keep notes, tasks, business, and chat work in Spaces, carry Context Bank with you, and open Nexus only when the business needs coverage, without losing a step.'
  },
  home: {
    // Negotiated council stack: Marketing H1 + product nouns + HCD permission in subhead.
    // SEO title/meta stay keyword-rich in page.tsx.
    // 2026-10-13 agentic review: keep H1; kill laws leftover; soften absolutes; Context Bank = custody.
    title: 'Never start over.',
    // Subhead job: nouns + context that is actionable and easy to reopen later.
    description:
      'AI workspace for notes, tasks, business, and chat work. Spaces keeps actionable context you can get back to easily later. Nexus for calls and follow-up when you need it.',
    whySwitchLabel: 'How Losi holds work',
    operatingModes: [
      {
        title: 'Search with context',
        description:
          'Look things up, compare sources, and keep the useful thread attached to the work instead of losing it in new tabs.'
      },
      {
        title: 'Create without switching tools',
        description:
          'Turn research into drafts, notes, visuals, and next steps in the same place you started.'
      },
      {
        title: 'Keep it all in Spaces',
        description:
          'Keep conversations, tasks, files, and notes organized in Spaces so the work is still usable tomorrow, next week, or by someone else.'
      }
    ],
    stats: [
      {
        value: 'One place',
        label: 'Notes, tasks, business, and chat work in a single flow'
      },
      {
        value: 'Spaces',
        label: 'Keep conversations, tasks, files, and notes organized in Spaces'
      },
      {
        value: 'Nexus',
        label: 'A business layer for calls, website chat, and follow-up when you need it'
      }
    ],
    startSection: {
      eyebrow: 'Who Losi is for',
      title: 'One workspace. Three ways in.',
      description:
        'Whether you run a shop or live in the work every day, Losi is built so you can put more in without watching it get wasted, forgotten, or reset.'
    },
    cards: [
      {
        title: 'For daily work',
        description:
          'It knows you. You never start over. Notes, chats, files, and plans stay attached so each session builds on the last.'
      },
      {
        title: 'For owners and operators',
        description:
          'Your business, handled. Nothing slips. Calls, follow-ups, and customer work stay in one place instead of evaporating across tools.'
      },
      {
        title: 'For teams that need shared context',
        description:
          'Give everyone one place to pick up research, drafts, tasks, and decisions, shared without surrendering ownership of the work.'
      }
    ],
    pillars: {
      // Brand voice lead: human trust, no manifesto laws.
      eyebrow: 'How Losi holds work',
      title: 'Context built to hold.',
      description:
        'Losi holds the workspace with you: Spaces, items, and the thread across conversations, so you are not rebuilding from a blank chat.',
      items: [
        {
          title: 'It knows across the workspace',
          description:
            'Losi can work across your Spaces and items the way a teammate who lived the project would. Context Bank holds that foundation across conversations, and you can bring it into other LLMs under your control.'
        },
        {
          title: 'It keeps the shape of the work',
          description:
            'Losi keeps the relationships between people, docs, tasks, and decisions so you never rebuild the floor every time you ask for help.'
        },
        {
          title: 'What’s yours stays yours',
          description:
            'Put the real material in. What’s shared, what’s private, and what you take with you stay under your control. You own the work; the platform doesn’t.'
        }
      ]
    },
    contextBank: {
      eyebrow: 'Context Bank',
      title: 'One foundation. Usable beyond Losi.',
      description:
        'Context Bank makes Losi’s context and capabilities usable to other LLMs over MCP or API, not only inside Losi. Projects, tasks, and work get a bigger place to live. Other tools may talk to the bank; what you put in stays yours.',
      capabilities: [
        'Memories',
        'Spaces',
        'Skills',
        'Soul',
        'Knowledge graph',
        'Nexus CRM',
        'Other LLMs',
        'MCP + API'
      ],
      learnMore: 'Learn more',
      cta: 'Start free'
    },
    connectors: {
      // Accumulation frame - connectors exist so nothing scatters, not as a SaaS feature parade.
      eyebrow: 'Connections',
      heading: 'Everything connects so nothing scatters.',
      subhead:
        'MCP servers, skills, and APIs feed one workspace that remembers. Work accumulates here instead of evaporating across tools that forget you. Need something custom? Losi builds the connector for you.',
      rowOne: [
        { name: 'GitHub', color: '#2743F0' },
        { name: 'Linear', color: '#16150F' },
        { name: 'Slack', color: '#5F7A5A' },
        { name: 'Postgres', color: '#B07A4F' },
        { name: 'Figma', color: '#5A6B7A' },
        { name: 'Stripe', color: '#8A6FB1' },
        { name: 'Sentry', color: '#2743F0' },
        { name: 'Brave Search', color: '#16150F' },
        { name: 'Playwright', color: '#5F7A5A' },
        { name: 'Shopify', color: '#B07A4F' },
        { name: 'HubSpot', color: '#5A6B7A' },
        { name: 'Airtable', color: '#8A6FB1' },
        { name: 'Jira', color: '#2743F0' },
        { name: 'Discord', color: '#16150F' }
      ],
      rowTwo: [
        { name: 'Google Drive', color: '#B07A4F' },
        { name: 'Salesforce', color: '#5A6B7A' },
        { name: 'Zendesk', color: '#8A6FB1' },
        { name: 'Intercom', color: '#2743F0' },
        { name: 'Asana', color: '#16150F' },
        { name: 'Confluence', color: '#5F7A5A' },
        { name: 'Telegram', color: '#B07A4F' },
        { name: 'SQLite', color: '#5A6B7A' },
        { name: 'Redis', color: '#8A6FB1' },
        { name: 'Docker', color: '#2743F0' },
        { name: 'Twilio', color: '#16150F' },
        { name: 'Cloudflare', color: '#5F7A5A' },
        { name: 'Mailchimp', color: '#B07A4F' },
        { name: '+ thousands more', color: '#2743F0', emphasize: true }
      ]
    },
    nexus: {
      eyebrow: 'Losi Nexus',
      title: 'Small team. Coverage that doesn’t quit.',
      description:
        'Nexus is force multiplication for service businesses, so a few people can answer every call, greet every visitor, book appointments, follow up on quotes, and log every outcome to CRM from the Spaces you already own. Unstoppable output without losing the thread.',
      panelLabel: 'Losi Nexus',
      panelTitle: '1 workspace',
      panelDescription:
        'No separate tools to babysit. Calls, bookings, follow-ups, and CRM run as Nexus agents inside your Losi Spaces, where a conversation can become a booking, a lead, or an agent action, and the record stays yours.',
      points: [
        'Answer calls and website chat with AI agents',
        'Book appointments and follow up on quotes',
        'Log outcomes to CRM from the Spaces you already own'
      ],
      cta: 'Explore Nexus',
      microcopy: 'Included in Losi Business plans.'
    },
    intake: {
      eyebrow: 'Try Losi live',
      title: 'Put real work in. Watch it keep building.',
      description:
        'Type a real task below. Take it somewhere useful, then keep the context so the next session picks up where you left off.',
      button: 'See the full experience',
      heroHintPrefix: 'Put work in. Ask Losi to',
      rotatingHints: [
        'draft a brief',
        'research with sources',
        'plan a Space',
        'summarize a PDF'
      ],
      placeholders: [
        'Ask me to draft, research, plan, or build something...',
        'Research a topic and cite the best sources...',
        'Turn my notes into a project plan in a Space...',
        'Draft a launch announcement from a few bullet points...'
      ],
      starterPrompts: [
        'Research a topic and cite the best sources',
        'Summarize this PDF and pull the key numbers',
        'Turn my notes into a project plan in a Space',
        'Draft a launch announcement from a few bullet points'
      ]
    },
    blogPanel: {
      guidesEyebrow: 'Keep building',
      guidesTitle: 'Ways to put more in and never lose it',
      guidesDescription:
        'Import context, open Spaces, and keep the thread so every session builds on the last.',
      label: 'Keep going',
      title: 'Bring what you already built. Don’t start over.',
      description:
        'Import the context you’ve earned. Open a Space. Let Tuesday know what Monday did.',
      cta: 'Start free',
      secondaryCta: 'View all guides'
    }
  },
  features: {
    eyebrow: 'Platform features',
    title: 'Everything you need so you never start over.',
    description:
      'Chat, search, Spaces, Context Bank, connectors, and Nexus in one operating surface, so daily work and service businesses keep context and convert answers into outcomes.',
    highlights: [
      {
        title: 'Search, chat, and create together',
        description:
          'Ask, find files and Spaces, then keep drafting without switching products.'
      },
      {
        title: 'Context Bank you can bring into other AIs',
        description:
          'Memories, Spaces, skills, and workspace context stay usable in Losi and other LLMs under your control, so projects and tasks share one foundation.'
      },
      {
        title: 'Spaces keeps projects organized',
        description:
          'Notes, tasks, files, and chats stay together so the work remains usable after the first answer.'
      },
      {
        title: 'Nexus when business needs coverage',
        description:
          'Calls, website chat, CRM, and follow-up when the work needs the business layer, not before.'
      }
    ],
    productMap: {
      eyebrow: 'What you get',
      title: 'One product map. Clear jobs for each surface.',
      description:
        'Start in chat, keep work in Spaces, carry context through Context Bank, connect tools, and open Nexus only when the business needs it.',
      items: [
        {
          title: 'Chat',
          description:
            'Multi-model chat with tools, files, web search, and a live try path that keeps the thread.'
        },
        {
          title: 'Search & find',
          description:
            'Find chats, files, Spaces, and Context Bank memories without rebuilding what you already know.'
        },
        {
          title: 'Spaces',
          description:
            'Projects with notes, tasks, sheets, events, and shared handoff for teams.'
        },
        {
          title: 'Context Bank',
          description:
            'Makes Losi context and capabilities usable to other LLMs via MCP, REST, API key, or OAuth, while what you put in stays yours.'
        },
        {
          title: 'Connectors',
          description:
            'Bring calendars, docs, CRM systems, and MCP servers into the same flow.'
        },
        {
          title: 'Nexus',
          description:
            'Business coverage for calls, website chat, CRM, campaigns, and AI readiness.'
        }
      ]
    },
    search: {
      eyebrow: 'Search and find',
      title: 'Find the thread, file, or Space without leaving the work.',
      description:
        'Losi search spans chats, Spaces, and Context Bank so you can pick up mid-project instead of starting over.',
      cards: [
        {
          title: 'Workspace-wide lookup',
          description:
            'Search Spaces, memories, skills, soul, graph, and Nexus records when your credentials allow it.'
        },
        {
          title: 'Keep what you find attached',
          description:
            'Open results next to chat so the next draft still has the reference in view.'
        },
        {
          title: 'Same bank for other LLMs',
          description:
            'MCP and API clients can run the same Context Bank search with API keys or OAuth, so you can bring that lookup into tools outside Losi without rebuilding context.'
        }
      ]
    },
    contextBank: {
      eyebrow: 'Context Bank',
      title: 'One foundation. Usable beyond Losi.',
      description:
        'Context Bank holds memories, Space knowledge, skills, soul, and the graph in one place, then makes that context and Losi’s capabilities usable to other LLMs over MCP or API. Projects, tasks, and work get a bigger foundation. Other tools may talk to the bank; what you put in stays yours.',
      cards: [
        {
          title: 'Bring the same bank into other tools',
          description:
            'Use Losi, Claude, Cursor, Codex, and more against the same bank through MCP or REST.'
        },
        {
          title: 'API key or OAuth',
          description:
            'Servers use workspace keys. Interactive apps open a grant page so users approve exact permissions.'
        },
        {
          title: 'Open-source SDK',
          description:
            'Start offline with @losi-ai packages, then connect hosted Context Bank when you want live workspace data.'
        }
      ],
      primary: 'Read the Context Bank guide',
      secondary: 'Start free'
    },
    core: {
      eyebrow: 'Chat and creation',
      title: 'Models, tools, and drafts that stay usable.',
      description:
        'Start with the task in front of you. Losi keeps research, drafts, files, and the next step connected.',
      cards: [
        {
          title: '40+ AI models',
          description:
            'Switch across leading models from OpenAI, Google, Anthropic, and more without leaving the thread.'
        },
        {
          title: 'Web search',
          description:
            'Pull live information into the chat so drafts stay grounded in what is current.'
        },
        {
          title: 'Content generation',
          description:
            'Draft text, code, images, and structured outputs that stay attached to the same conversation.'
        },
        {
          title: 'Enterprise-grade security',
          description:
            'Keep conversations and files protected with encryption, access controls, and private handling by default.'
        }
      ]
    },
    creative: {
      eyebrow: 'Creative tools',
      title: 'Creative AI studio',
      description:
        'Generate video, music, audio transcripts, and 3D assets without leaving the same workspace.',
      cards: [
        {
          title: 'Video generation',
          description: 'Create clips and animate stills for storyboards and shareable drafts.'
        },
        {
          title: 'Music generation',
          description: 'Produce original tracks and beds for videos, decks, and demos.'
        },
        {
          title: 'Audio transcription',
          description: 'Upload voice notes and get transcripts you can edit, search, and reuse.'
        },
        {
          title: '3D assets',
          description: 'Generate interactive 3D models and assets from references and prompts.'
        }
      ]
    },
    workflows: {
      eyebrow: 'Workflows and learning',
      title: 'Turn answers into action without leaving the flow.',
      description:
        'Study, ship, or hand work off after the first answer, not only generate it.',
      cards: [
        {
          title: 'Interactive flashcards',
          description:
            'Turn notes into flippable study cards with spaced practice instead of starting a new tool.'
        },
        {
          title: 'Quiz sessions',
          description:
            'Generate quizzes with scoring so learning stays next to the source material.'
        },
        {
          title: 'Mind maps',
          description:
            'Map ideas, timelines, and relationships visually while the chat context stays attached.'
        },
        {
          title: 'Custom AI assistants',
          description:
            'Create assistants with tools, files, and instructions tailored to recurring work.'
        }
      ]
    },
    spaces: {
      eyebrow: 'Losi Spaces',
      title: 'Spaces keeps the work from disappearing after the answer.',
      description:
        'Spaces turns chats and files into shared, retrievable project context.',
      cards: [
        {
          title: 'Project context that stays attached',
          description:
            'Keep research, drafts, files, and decisions together so your team does not rebuild context from old chats.'
        },
        {
          title: 'Notes and tasks beside the work',
          description:
            'Turn ideas into action because notes and tasks live next to the AI work that created them.'
        },
        {
          title: 'Shared handoff for teams',
          description:
            'Move from individual prompts to shared execution with a place to pick up together.'
        }
      ]
    },
    assistants: {
      eyebrow: 'Assistants'
    },
    nexus: {
      eyebrow: 'Losi Nexus',
      title: 'Nexus is the business side of Losi.',
      description:
        'Calls, website chat, CRM, campaigns, and AI readiness when the business needs coverage. The rest of Losi stays broad for study, freelance, and team workflows.',
      cards: [
        {
          title: 'Calls and website chat',
          description:
            'Cover high-intent callers and site visitors when the team is busy.'
        },
        {
          title: 'CRM and follow-up',
          description:
            'Keep leads, contacts, activities, and outbound follow-up connected to Spaces.'
        },
        {
          title: 'AI readiness',
          description:
            'See where AI should help first before buying another tool or starting another pilot.'
        }
      ]
    },
    cta: {
      eyebrow: 'Ready to explore',
      title: 'Safe to go all in.',
      description:
        'Start in chat, keep notes, tasks, business, and chat work in Spaces, carry Context Bank with you, and open Nexus only when the business needs coverage, without losing a step.',
      primary: 'Start free',
      secondary: 'See Losi Nexus',
      tertiary: 'Read the docs'
    }
  },
  enterprise: {
    eyebrow: 'Losi Nexus for business',
    title: 'For service businesses missing calls and chats, keep the first response covered.',
    description:
      'Losi Nexus gives service businesses a faster first response across calls, website chat, and follow-up so leads and customers do not wait on a busy team.',
    benefitCards: [
      {
        title: 'Answer more calls',
        description:
          'Keep high-intent callers from slipping away when the front desk or team is busy.'
      },
      {
        title: 'Handle website chat faster',
        description: 'Give visitors a faster path to booking, support, or sales.'
      },
      {
        title: 'Automate follow-up',
        description: 'Keep confirmations, reminders, and next steps moving without manual effort.'
      },
      {
        title: 'Support the team',
        description:
          'Give staff a steadier first-response layer instead of forcing every request into the same queue.'
      }
    ],
    why: {
      eyebrow: 'Why Losi Nexus',
      title: 'Built for businesses that need more coverage without more chaos.',
      description:
        'Use Nexus when missed calls, slow callbacks, or scattered tools are creating avoidable revenue loss or service drag.'
    },
    includes: {
      eyebrow: 'What it includes',
      title: 'Cover the first response. Get a clearer AI starting point.',
      description:
        'Nexus covers calls, chat, bookings, and follow-through now. AI readiness gives you a company-email way to see where automation should help next.',
      panelLabel: 'Included with the business plans',
      panelTitle: 'Plans built for response, coordination, and growth.',
      points: [
        'Built for service businesses that need faster response times',
        'Pairs AI agents with shared Spaces and Losi Pro seats',
        'Connects voice, chat, bookings, workflows, and follow-up',
        'Includes AI Readiness for teams deciding where to start'
      ],
      readinessLabel: 'Before the rollout',
      readinessTitle: 'Check AI readiness with the company email tied to the business.',
      readinessDescription:
        'Use the inbox your company already owns. We match the domain, prepare the report in the background, and send it back when it is ready.'
    },
    plans: {
      eyebrow: 'Plans',
      title: 'Choose the business plan that fits your stage.',
      description:
        'Start with the response layer you need now, then expand when security, procurement, or custom workflows need more structure.'
    }
  },
  aiReadiness: {
    intro: {
      eyebrow: 'AI readiness for real businesses',
      title:
        'For teams unsure where AI should start, check AI readiness before you buy another tool.',
      description:
        'Use a verified company email, let us review the business tied to that domain, and we will send the completed report once it is ready.'
    },
    trustPoints: [
      {
        title: 'Made for real companies',
        description:
          'Use a company email so the review stays tied to the business you actually want assessed.'
      },
      {
        title: 'Prepared in the background',
        description:
          'The report is prepared after submission and sent when it is ready instead of forcing a generic instant score.'
      },
      {
        title: 'Built to guide the next step',
        description:
          'Use the report to decide where AI should help first before your team buys more software or starts another pilot.'
      }
    ],
    expectations: {
      eyebrow: 'What to expect',
      title: 'A stronger starting point than a generic instant score.',
      description:
        'You are not buying software on this page. You are getting a clearer view of where AI should help first and what to prioritize next.',
      panelLabel: 'Why it works',
      panelTitle: 'Company-specific because the domain matters.',
      body: [
        'A verified company email helps us connect the review to the right business and send the finished report to the right inbox.',
        'Once the report is prepared, it becomes easier to share, discuss, and use as a starting point for practical AI decisions.'
      ]
    },
    faqLabel: 'FAQ',
    faqs: [
      {
        question: 'Why do you require a company email?',
        answer:
          'The work email helps us identify the right business and gives us the verified inbox where the finished report should be delivered.'
      },
      {
        question: 'What if our main site is on another domain?',
        answer:
          'Use the optional website override field. We will still use the company email for delivery, but review the site you specify.'
      },
      {
        question: 'Will I see the full report instantly on this page?',
        answer: 'No. We prepare the full report in the background and email it once it is ready.'
      }
    ]
  },
  leadMagnet: {
    label: 'AI readiness',
    title: 'Check AI readiness with your company email.',
    description:
      'Enter a company email and we will review the business behind it, prepare a tailored AI Readiness report, and send the finished result to that inbox when it is ready.',
    valueCards: [
      {
        title: 'Company-specific review',
        description: 'We analyze the public business behind the company email you submit.'
      },
      {
        title: 'Prepared in the background',
        description: 'Your report is generated after submission and emailed once it is ready.'
      },
      {
        title: 'Clearer next step',
        description: 'Use the report to decide where AI should help first across the business.'
      }
    ],
    afterSubmitLabel: 'After you submit',
    afterSubmitSteps: [
      'We match your company email to the right business domain.',
      'We prepare your AI Readiness review in the background.',
      'We email the finished report to your verified inbox.'
    ],
    form: {
      eyebrow: 'Verified company email',
      title: 'Check AI readiness for your company',
      description:
        'Enter your name, company email, and role. We will review the matching business domain and email the finished AI Readiness report once it is ready.',
      modalTitle: 'Check AI readiness with a company email',
      modalDescription:
        'Enter the company inbox tied to the business. We will prepare the report in the background and send it there once it is ready.',
      fullName: 'Full name',
      fullNamePlaceholder: 'Jordan Avery',
      role: 'Role or profession in the company',
      rolePlaceholder: 'Operations Manager',
      companyEmail: 'Company email',
      companyEmailPlaceholder: 'team@yourcompany.com',
      companyDomain: 'Company domain',
      companyDomainBadge: 'Company domain: {{domain}}',
      useCompanyInbox: 'Use a company inbox',
      freeMailHint: 'Free-mail inboxes are blocked so we can review the right company.',
      websiteOverride: 'Website override',
      optional: 'Optional',
      websitePlaceholder: 'https://yourmainsite.com',
      websiteHint:
        'Use this only if your main company website lives on a different domain than your email.',
      deliveryCheckbox:
        'I confirm this is my business email and I want my AI Readiness evaluation sent here.',
      marketingCheckbox:
        'I agree to receive the AI Readiness evaluation and related follow-up communications. I can unsubscribe at any time.',
      modalConsentCheckbox:
        'I verify this is the right company email for my company and agree to receive the AI readiness report and related follow-up here.',
      backgroundHint:
        'We prepare the report in the background and send it to the verified company inbox once it is ready.',
      preparingStatusLabel: 'Preparing now',
      preparingStatusDescription:
        'We are matching the company domain, checking the public footprint, and building the report now.',
      submit: 'Check AI readiness',
      submitting: 'Preparing your report',
      successLabel: 'Now preparing',
      successTitle: 'Your AI Readiness report is on the way',
      successDescription:
        'We are preparing the AI Readiness report for {{businessName}} by reviewing {{domain}}. Once it is ready, we will send it to {{email}}.',
      checkAnother: 'Check another company',
      enterName: 'Enter your full name.',
      enterRole: 'Enter your role or profession in the company.',
      enterEmail: 'Enter your company email.',
      enterValidEmail: 'Enter a valid company email.',
      restrictedDomain: 'Use your company email so we can evaluate the correct business domain.',
      confirmDelivery: 'Confirm your business email before continuing.',
      consentRequired: 'Consent is required before we can send the evaluation.',
      submitError: 'Failed to generate your AI readiness evaluation.'
    }
  },
  support: {
    eyebrow: 'Support',
    title: 'Talk to the Losi team.',
    description:
      'Need help with your account, billing, or setup? Send the details once and we will route it to the right person.',
    includeTitle: 'What to include',
    includeItems: [
      'A clear subject line so we can route the request properly',
      'The email tied to your Losi account if the issue is account-specific',
      'Enough detail for us to understand what happened and what you expected'
    ],
    fastTitle: 'Need help fast?',
    fastDescription:
      'If your note is about a specific page or workflow, use the subject and message query params to prefill this form from elsewhere in the product.',
    name: 'Name',
    email: 'Email',
    subject: 'Subject',
    message: 'Message',
    namePlaceholder: 'Jordan Avery',
    emailPlaceholder: 'you@company.com',
    subjectPlaceholder: 'How can we help?',
    messagePlaceholder: 'Tell us what happened and what you need help with.'
  },
  canada: {
    eyebrow: 'Made in Canada',
    title: 'Canadian values, modern AI ambition.',
    description:
      'Losi reflects a Canadian approach to technology: practical, inclusive, privacy-conscious, and built to serve real people and real businesses.',
    practiceLabel: 'What that means in practice',
    practiceItems: [
      'A product story that values clarity over hype',
      'Inclusive language support across a wide range of communities',
      'Stronger attention to trust, privacy, and responsible growth'
    ],
    principles: {
      eyebrow: 'Principles',
      title: 'The product story is shaped by more than performance alone.',
      description:
        'Being Canadian is not a badge added at the end. It influences how Losi thinks about usefulness, language, trust, and long-term value.',
      cards: [
        {
          title: 'Built with a Canadian point of view',
          description:
            'Losi is positioned around practical usefulness, trust, and a more grounded approach to AI adoption.'
        },
        {
          title: 'Language and cultural breadth',
          description:
            'The product is designed for diverse teams and communities, with support for a wide range of languages and use cases.'
        },
        {
          title: 'Privacy-forward by design',
          description:
            'Respect for data, control, and responsible product decisions is part of how the platform is presented and built.'
        },
        {
          title: 'Thoughtful product direction',
          description:
            'We focus on durable, useful workflows instead of short-lived AI gimmicks that create noise without real value.'
        }
      ]
    },
    language: {
      eyebrow: 'Language support',
      title: 'A broader view of who AI should be built for.',
      description:
        'Losi aims to support a wider range of languages and communities, including attention to Indigenous language representation as part of a more inclusive future.',
      label: 'Indigenous language recognition',
      panelTitle: 'Preserve more than utility. Preserve cultural presence.',
      panelDescription:
        'Supporting a broad language footprint is part of making AI feel more representative, more useful, and more respectful of the communities it is meant to serve.'
    }
  }
} as const

type MarketingCopy = typeof englishMarketingCopy

const marketingCopyByLocale: Record<MarketingLocale, MarketingCopy> = {
  en: englishMarketingCopy,
  es: {
    common: {
      home: 'Inicio',
      features: 'Funciones',
      docs: 'Docs',
      importAi: 'Importar',
      aiReadiness: 'Preparacion para IA',
      agents: 'Negocios',
      madeInCanada: 'Hecho en Canada',
      support: 'Soporte',
      privacy: 'Privacidad',
      terms: 'Terminos',
      startFree: 'Empieza gratis',
      logIn: 'Iniciar sesion',
      exploreFeatures: 'Explora las funciones',
      seeHowLosiWorks: 'Mira como funciona Losi',
      seePlans: 'Ver planes',
      seeLosiNexus: 'Explorar Nexus',
      checkAiReadiness: 'Comprobar preparacion para IA',
      explorePlatform: 'Explorar la plataforma',
      openApp: 'Abrir la app',
      cancel: 'Cancelar',
      sendMessage: 'Enviar mensaje',
      sending: 'Enviando',
      supportSuccess: 'Solicitud enviada correctamente. Te responderemos pronto.',
      supportFillAll: 'Completa todos los campos',
      supportEmailInvalid: 'Introduce un correo valido',
      supportSubmitError: 'No se pudo enviar la solicitud de soporte'
    },
    footer: {
      platformTitle: 'Plataforma',
      companyTitle: 'Empresa',
      legalTitle: 'Legal',
      studioLabel: 'Marshmallow Studio',
      tagline:
        'El martes sabe lo que hizo el lunes. Tu trabajo permanece, crece y te pertenece.',
      ctaTitle: 'Seguro para entregarte por completo.',
      ctaDescription:
        'Empieza en el chat, mantén notas, tareas, negocio y trabajo de chat en Spaces, lleva Context Bank contigo y abre Nexus solo cuando el negocio lo necesite, sin perder un paso.'
    },
    home: {
      title: 'Nunca empieces de cero.',
      description:
        'Espacio de trabajo de IA para notas, tareas, negocio y trabajo de chat. Spaces guarda contexto accionable al que puedes volver facilmente despues. Nexus para llamadas y seguimiento cuando lo necesites.',
      whySwitchLabel: 'Como Losi sostiene el trabajo',
      operatingModes: [
        {
          title: 'Busca con contexto',
          description:
            'Busca, compara fuentes y conserva el hilo util unido al trabajo en vez de perderlo en nuevas pestanas.'
        },
        {
          title: 'Crea sin cambiar de herramienta',
          description:
            'Convierte investigacion en borradores, notas, visuales y siguientes pasos en el mismo lugar donde empezaste.'
        },
        {
          title: 'Guardalo todo en Spaces',
          description:
            'Mantén conversaciones, tareas, archivos y notas organizados en Spaces para que el trabajo siga siendo util manana, la proxima semana o para otra persona.'
        }
      ],
      stats: [
        {
          value: 'Un solo lugar',
          label: 'Notas, tareas, negocio y trabajo de chat en un mismo flujo'
        },
        {
          value: 'Spaces',
          label: 'Mantén conversaciones, tareas, archivos y notas organizados en Spaces'
        },
        {
          value: 'Nexus',
          label: 'Una capa empresarial para llamadas, chat web y seguimiento cuando la necesites'
        }
      ],
      startSection: {
        eyebrow: 'Para quien es Losi',
        title: 'Un workspace. Tres formas de entrar.',
        description:
          'Ya dirijas un negocio o vivas en el trabajo cada dia, Losi esta hecho para que puedas meter mas sin verlo desperdiciarse, olvidarse o reiniciarse.'
      },
      cards: [
        {
          title: 'Para el trabajo diario',
          description:
            'Te conoce. Nunca empiezas de nuevo. Notas, chats, archivos y planes quedan unidos para que cada sesion construya sobre la anterior.'
        },
        {
          title: 'Para duenos y operadores',
          description:
            'Tu negocio, atendido. Nada se escapa. Llamadas, seguimientos y trabajo con clientes viven en un solo lugar en vez de evaporarse entre herramientas.'
        },
        {
          title: 'Para equipos que necesitan contexto compartido',
          description:
            'Da a todos un solo lugar para retomar investigacion, borradores, tareas y decisiones, compartido sin ceder la propiedad del trabajo.'
        }
      ],
      pillars: {
        eyebrow: 'Como Losi sostiene el trabajo',
        title: 'Contexto hecho para sostener.',
        description:
          'Losi sostiene el workspace contigo: Spaces, elementos y el hilo entre conversaciones, para que no reconstruyas desde un chat en blanco.',
        items: [
          {
            title: 'Conoce a traves del workspace',
            description:
              'Losi puede trabajar en tus Spaces y elementos como un companero que vivio el proyecto. Context Bank guarda esa base entre conversaciones, y puedes llevarla a otras LLMs bajo tu control.'
          },
          {
            title: 'Conserva la forma del trabajo',
            description:
              'Losi guarda las relaciones entre personas, documentos, tareas y decisiones para que nunca reconstruyas el suelo cada vez que pides ayuda.'
          },
          {
            title: 'Lo tuyo se queda tuyo',
            description:
              'Mete el material de verdad. Lo compartido, lo privado y lo que te llevas siguen bajo tu control. Tu eres dueno del trabajo; la plataforma no.'
          }
        ]
      },
      contextBank: {
        eyebrow: 'Context Bank',
        title: 'Una base. Usable fuera de Losi.',
        description:
          'Context Bank hace que el contexto y las capacidades de Losi sean usables por otras LLMs por MCP o API, no solo dentro de Losi. Proyectos, tareas y trabajo tienen un lugar mas grande donde vivir. Otras herramientas pueden hablar con el banco; lo que metes sigue siendo tuyo.',
        capabilities: [
          'Memorias',
          'Spaces',
          'Skills',
          'Soul',
          'Grafo de conocimiento',
          'Nexus CRM',
          'Otras LLMs',
          'MCP + API'
        ],
        learnMore: 'Saber mas',
        cta: 'Empezar gratis'
      },
      connectors: {
        eyebrow: 'Conexiones',
        heading: 'Todo se conecta para que nada se disperse.',
        subhead:
          'Servidores MCP, skills y APIs alimentan un solo workspace que recuerda. El trabajo se acumula aqui en vez de evaporarse en herramientas que te olvidan. Necesitas algo a medida? Losi construye el conector por ti.',
        rowOne: [
          { name: 'GitHub', color: '#2743F0' },
          { name: 'Linear', color: '#16150F' },
          { name: 'Slack', color: '#5F7A5A' },
          { name: 'Postgres', color: '#B07A4F' },
          { name: 'Figma', color: '#5A6B7A' },
          { name: 'Stripe', color: '#8A6FB1' },
          { name: 'Sentry', color: '#2743F0' },
          { name: 'Brave Search', color: '#16150F' },
          { name: 'Playwright', color: '#5F7A5A' },
          { name: 'Shopify', color: '#B07A4F' },
          { name: 'HubSpot', color: '#5A6B7A' },
          { name: 'Airtable', color: '#8A6FB1' },
          { name: 'Jira', color: '#2743F0' },
          { name: 'Discord', color: '#16150F' }
        ],
        rowTwo: [
          { name: 'Google Drive', color: '#B07A4F' },
          { name: 'Salesforce', color: '#5A6B7A' },
          { name: 'Zendesk', color: '#8A6FB1' },
          { name: 'Intercom', color: '#2743F0' },
          { name: 'Asana', color: '#16150F' },
          { name: 'Confluence', color: '#5F7A5A' },
          { name: 'Telegram', color: '#B07A4F' },
          { name: 'SQLite', color: '#5A6B7A' },
          { name: 'Redis', color: '#8A6FB1' },
          { name: 'Docker', color: '#2743F0' },
          { name: 'Twilio', color: '#16150F' },
          { name: 'Cloudflare', color: '#5F7A5A' },
          { name: 'Mailchimp', color: '#B07A4F' },
          { name: '+ miles mas', color: '#2743F0', emphasize: true }
        ]
      },
      nexus: {
        eyebrow: 'Losi Nexus',
        title: 'Equipo pequeno. Cobertura que no para.',
        description:
          'Nexus es multiplicacion de fuerza para negocios de servicios, para que pocas personas puedan atender cada llamada, recibir a cada visitante, reservar citas, seguir presupuestos y registrar cada resultado en el CRM desde los Spaces que ya poseen. Salida imparable sin perder el hilo.',
        panelLabel: 'Losi Nexus',
        panelTitle: '1 espacio de trabajo',
        panelDescription:
          'Sin herramientas aparte que cuidar. Llamadas, reservas, seguimientos y CRM corren como agentes de Nexus dentro de tus Spaces de Losi, donde una conversacion puede convertirse en una reserva, un lead o una accion de agente, y el registro sigue siendo tuyo.',
        points: [
          'Atiende llamadas y chat web con agentes de IA',
          'Reserva citas y haz seguimiento de presupuestos',
          'Registra resultados en el CRM desde los Spaces que ya posees'
        ],
        cta: 'Explorar Nexus',
        microcopy: 'Incluido en los planes Losi Business.'
      },
      intake: {
        eyebrow: 'Prueba Losi en vivo',
        title: 'Mete trabajo real. Observa como sigue creciendo.',
        description:
          'Escribe una tarea real abajo. Llevala a un resultado util, y conserva el contexto para retomar donde lo dejaste.',
        button: 'Ver la experiencia completa',
        heroHintPrefix: 'Mete trabajo. Pidele a Losi que',
        rotatingHints: [
          'redacte un brief',
          'investigue con fuentes',
          'planifique un Space',
          'resuma un PDF'
        ],
        placeholders: [
          'Pideme redactar, investigar, planificar o construir algo...',
          'Investiga un tema y cita las mejores fuentes...',
          'Convierte mis notas en un plan de proyecto en un Space...',
          'Redacta un anuncio de lanzamiento a partir de unas viñetas...'
        ],
        starterPrompts: [
          'Investiga un tema y cita las mejores fuentes',
          'Resume este PDF y saca los numeros clave',
          'Convierte mis notas en un plan de proyecto en un Space',
          'Redacta un anuncio de lanzamiento a partir de unas viñetas'
        ]
      },
      blogPanel: {
        guidesEyebrow: 'Sigue construyendo',
        guidesTitle: 'Formas de meter mas y no perderlo',
        guidesDescription:
          'Importa contexto, abre Spaces y mantén el hilo para que cada sesión construya sobre la anterior.',
        label: 'Sigue adelante',
        title: 'Trae lo que ya construiste. No empieces de cero.',
        description:
          'Importa el contexto que ya ganaste. Abre un Space. Que el martes sepa lo que hizo el lunes.',
        cta: 'Empezar gratis',
        secondaryCta: 'Ver todas las guias'
      }
    },
    features: {
      eyebrow: 'Funciones de la plataforma',
      title: 'Todo lo que necesitas para no empezar de cero.',
      description:
        'Chat, busqueda, Spaces, Context Bank, conectores y Nexus en una sola superficie, para que el trabajo diario y los negocios de servicios conserven contexto y conviertan respuestas en resultados.',
      highlights: [
        {
          title: 'Busca, chatea y crea juntos',
          description:
            'Pregunta, encuentra archivos y Spaces, y sigue redactando sin cambiar de producto.'
        },
        {
          title: 'Context Bank que puedes llevar a otras IAs',
          description:
            'Memorias, Spaces, skills y contexto del workspace usables en Losi y en otras LLMs bajo tu control, para que proyectos y tareas compartan una sola base.'
        },
        {
          title: 'Spaces mantiene los proyectos ordenados',
          description:
            'Notas, tareas, archivos y chats juntos para que el trabajo siga siendo util despues de la primera respuesta.'
        },
        {
          title: 'Nexus cuando el negocio necesita cobertura',
          description:
            'Llamadas, chat web, CRM y seguimiento cuando el trabajo necesita la capa empresarial, no antes.'
        }
      ],
      productMap: {
        eyebrow: 'Que incluye',
        title: 'Un mapa de producto. Trabajos claros para cada superficie.',
        description:
          'Empieza en chat, guarda el trabajo en Spaces, lleva contexto con Context Bank, conecta herramientas y abre Nexus solo cuando el negocio lo necesite.',
        items: [
          {
            title: 'Chat',
            description:
              'Chat multi-modelo con herramientas, archivos, busqueda web y un camino de prueba que conserva el hilo.'
          },
          {
            title: 'Busqueda',
            description:
              'Encuentra chats, archivos, Spaces y memorias del Context Bank sin reconstruir lo que ya sabes.'
          },
          {
            title: 'Spaces',
            description:
              'Proyectos con notas, tareas, hojas, eventos y handoff compartido para equipos.'
          },
          {
            title: 'Context Bank',
            description:
              'Hace usables el contexto y las capacidades de Losi para otras LLMs via MCP, REST, API key u OAuth, mientras lo que metes sigue siendo tuyo.'
          },
          {
            title: 'Conectores',
            description:
              'Trae calendarios, docs, CRM y servidores MCP al mismo flujo.'
          },
          {
            title: 'Nexus',
            description:
              'Cobertura de negocio para llamadas, chat web, CRM, campañas y AI readiness.'
          }
        ]
      },
      search: {
        eyebrow: 'Busqueda',
        title: 'Encuentra el hilo, archivo o Space sin salir del trabajo.',
        description:
          'La busqueda de Losi abarca chats, Spaces y Context Bank para retomar a mitad de proyecto.',
        cards: [
          {
            title: 'Busqueda en todo el workspace',
            description:
              'Busca Spaces, memorias, skills, soul, grafo y registros Nexus cuando tus credenciales lo permitan.'
          },
          {
            title: 'Conserva lo que encuentras',
            description:
              'Abre resultados junto al chat para que el siguiente borrador siga teniendo la referencia a la vista.'
          },
          {
            title: 'El mismo banco para otras LLMs',
            description:
              'Clientes MCP y API pueden usar la misma busqueda del Context Bank con API key u OAuth, para llevar esa busqueda a herramientas fuera de Losi sin reconstruir contexto.'
          }
        ]
      },
      contextBank: {
        eyebrow: 'Context Bank',
        title: 'Una base. Usable fuera de Losi.',
        description:
          'Context Bank guarda memorias, conocimiento de Spaces, skills, soul y el grafo en un solo lugar, y hace que ese contexto y las capacidades de Losi sean usables por otras LLMs por MCP o API. Proyectos, tareas y trabajo tienen una base mas grande. Otras herramientas pueden hablar con el banco; lo que metes sigue siendo tuyo.',
        cards: [
          {
            title: 'Lleva el mismo banco a otras herramientas',
            description:
              'Usa Losi, Claude, Cursor, Codex y mas contra el mismo banco por MCP o REST.'
          },
          {
            title: 'API key u OAuth',
            description:
              'Los servidores usan claves de workspace. Las apps interactivas abren una pagina de permisos.'
          },
          {
            title: 'SDK open source',
            description:
              'Empieza offline con paquetes @losi-ai y conecta el Context Bank hospedado cuando quieras datos en vivo.'
          }
        ],
        primary: 'Leer la guia de Context Bank',
        secondary: 'Empezar gratis'
      },
      core: {
        eyebrow: 'Chat y creacion',
        title: 'Modelos, herramientas y borradores que siguen sirviendo.',
        description:
          'Empieza con la tarea que tienes delante. Losi mantiene investigacion, borradores, archivos y el siguiente paso conectados.',
        cards: [
          {
            title: 'Mas de 40 modelos de IA',
            description:
              'Cambia entre modelos de OpenAI, Google, Anthropic y mas sin salir del hilo.'
          },
          {
            title: 'Busqueda web',
            description:
              'Trae informacion en vivo al chat para que los borradores se basen en lo actual.'
          },
          {
            title: 'Generacion de contenido',
            description:
              'Redacta texto, codigo, imagenes y salidas estructuradas en la misma conversacion.'
          },
          {
            title: 'Seguridad de nivel empresarial',
            description:
              'Protege conversaciones y archivos con cifrado, controles de acceso y manejo privado.'
          }
        ]
      },
      creative: {
        eyebrow: 'Herramientas creativas',
        title: 'Estudio creativo de IA',
        description:
          'Genera video, musica, transcripciones y assets 3D sin salir del mismo workspace.',
        cards: [
          {
            title: 'Generacion de video',
            description: 'Crea clips y anima imagenes para storyboards y borradores compartibles.'
          },
          {
            title: 'Generacion de musica',
            description: 'Produce pistas originales para videos, decks y demos.'
          },
          {
            title: 'Transcripcion de audio',
            description: 'Sube notas de voz y obtén transcripciones editables y buscables.'
          },
          {
            title: 'Assets 3D',
            description: 'Genera modelos 3D interactivos a partir de referencias y prompts.'
          }
        ]
      },
      workflows: {
        eyebrow: 'Flujos y aprendizaje',
        title: 'Convierte respuestas en accion sin salir del flujo.',
        description:
          'Estudia, entrega o cede trabajo despues de la primera respuesta, no solo la generes.',
        cards: [
          {
            title: 'Flashcards interactivas',
            description:
              'Convierte notas en tarjetas de estudio con practica espaciada sin otra herramienta.'
          },
          {
            title: 'Sesiones de quiz',
            description:
              'Genera quizzes con puntuacion junto al material de origen.'
          },
          {
            title: 'Mapas mentales',
            description:
              'Visualiza ideas, lineas de tiempo y relaciones con el contexto del chat intacto.'
          },
          {
            title: 'Asistentes de IA personalizados',
            description:
              'Crea asistentes con herramientas, archivos e instrucciones para trabajo recurrente.'
          }
        ]
      },
      spaces: {
        eyebrow: 'Losi Spaces',
        title: 'Spaces evita que el trabajo desaparezca despues de la respuesta.',
        description:
          'Spaces convierte chats y archivos en contexto compartido que se puede recuperar.',
        cards: [
          {
            title: 'Contexto de proyecto que permanece',
            description:
              'Mantén investigacion, borradores, archivos y decisiones juntos.'
          },
          {
            title: 'Notas y tareas junto al trabajo',
            description:
              'Convierte ideas en accion porque notas y tareas viven junto al trabajo de IA.'
          },
          {
            title: 'Handoff compartido para equipos',
            description:
              'Pasa de prompts individuales a ejecucion compartida.'
          }
        ]
      },
      assistants: {
        eyebrow: 'Asistentes'
      },
      nexus: {
        eyebrow: 'Losi Nexus',
        title: 'Nexus es el lado empresarial de Losi.',
        description:
          'Llamadas, chat web, CRM, campañas y AI readiness cuando el negocio necesita cobertura.',
        cards: [
          {
            title: 'Llamadas y chat web',
            description:
              'Cubre llamadas de alta intencion y visitantes del sitio cuando el equipo esta ocupado.'
          },
          {
            title: 'CRM y seguimiento',
            description:
              'Mantén leads, contactos, actividades y outbound conectados a Spaces.'
          },
          {
            title: 'AI readiness',
            description:
              'Ve donde debe ayudar primero la IA antes de comprar otra herramienta.'
          }
        ]
      },
      cta: {
        eyebrow: 'Listo para explorar',
        title: 'Seguro para entregarte por completo.',
        description:
          'Empieza en el chat, mantén notas, tareas, negocio y trabajo de chat en Spaces, lleva Context Bank contigo y abre Nexus solo cuando el negocio lo necesite, sin perder un paso.',
        primary: 'Empieza gratis',
        secondary: 'Ver Losi Nexus',
        tertiary: 'Leer la documentacion'
      }
    },
    enterprise: {
      eyebrow: 'Losi Nexus para empresas',
      title: 'Para empresas de servicios que pierden llamadas y chats, cubre la primera respuesta.',
      description:
        'Losi Nexus da a las empresas de servicios una primera respuesta mas rapida en llamadas, chat web y seguimiento para que clientes y oportunidades no esperen a un equipo saturado.',
      benefitCards: [
        {
          title: 'Responder mas llamadas',
          description:
            'Evita perder llamadas de alta intencion cuando recepcion o el equipo estan ocupados.'
        },
        {
          title: 'Atender mas rapido el chat web',
          description: 'Da a los visitantes una ruta mas rapida hacia reserva, soporte o ventas.'
        },
        {
          title: 'Automatizar el seguimiento',
          description:
            'Mantén confirmaciones, recordatorios y siguientes pasos en movimiento sin esfuerzo manual.'
        },
        {
          title: 'Apoyar al equipo',
          description:
            'Da al personal una capa inicial mas estable en lugar de empujar todo a la misma cola.'
        }
      ],
      why: {
        eyebrow: 'Por que Losi Nexus',
        title: 'Hecho para empresas que necesitan mas cobertura sin mas caos.',
        description:
          'Usa Nexus cuando las llamadas perdidas, las devoluciones tardias o las herramientas dispersas ya esten causando perdida evitable de ingresos o servicio.'
      },
      includes: {
        eyebrow: 'Que incluye',
        title: 'Cubre la primera respuesta. Consigue un punto de partida mas claro para IA.',
        description:
          'Nexus cubre llamadas, chat, reservas y seguimiento ahora. AI Readiness te da una forma basada en correo corporativo para ver donde debe ayudar la automatizacion despues.',
        panelLabel: 'Incluido en los planes empresariales',
        panelTitle: 'Planes pensados para respuesta, coordinacion y crecimiento.',
        points: [
          'Hecho para empresas de servicios que necesitan tiempos de respuesta mas rapidos',
          'Combina agentes de IA con Spaces compartidos y asientos Losi Pro',
          'Conecta voz, chat, reservas, flujos y seguimiento',
          'Incluye AI Readiness para equipos que deciden por donde empezar'
        ],
        readinessLabel: 'Antes del despliegue',
        readinessTitle: 'Comprueba AI Readiness con el correo corporativo ligado al negocio.',
        readinessDescription:
          'Usa el inbox que la empresa ya tiene. Relacionamos el dominio, preparamos el informe en segundo plano y lo enviamos cuando este listo.'
      },
      plans: {
        eyebrow: 'Planes',
        title: 'Elige el plan empresarial que encaja con tu etapa.',
        description:
          'Empieza con la capa de respuesta que necesitas hoy y expande cuando seguridad, compras o flujos personalizados pidan mas estructura.'
      }
    },
    aiReadiness: {
      intro: {
        eyebrow: 'AI Readiness para empresas reales',
        title:
          'Para equipos que no saben por donde empezar con IA, comprueba AI Readiness antes de comprar otra herramienta.',
        description:
          'Usa un correo corporativo verificado, deja que revisemos el negocio ligado a ese dominio y enviaremos el informe completo cuando este listo.'
      },
      trustPoints: [
        {
          title: 'Hecho para empresas reales',
          description:
            'Usa un correo corporativo para que la revision quede ligada al negocio que realmente quieres evaluar.'
        },
        {
          title: 'Preparado en segundo plano',
          description:
            'El informe se prepara despues del envio y se entrega cuando esta listo en lugar de forzar una puntuacion instantanea generica.'
        },
        {
          title: 'Pensado para definir el siguiente paso',
          description:
            'Usa el informe para decidir donde debe ayudar primero la IA antes de comprar mas software o lanzar otro piloto.'
        }
      ],
      expectations: {
        eyebrow: 'Que esperar',
        title: 'Un punto de partida mas fuerte que una puntuacion instantanea generica.',
        description:
          'No estas comprando software en esta pagina. Estas consiguiendo una vision mas clara de donde debe ayudar primero la IA y que conviene priorizar despues.',
        panelLabel: 'Por que funciona',
        panelTitle: 'Especifico para la empresa porque el dominio importa.',
        body: [
          'Un correo empresarial verificado nos ayuda a conectar la revision con el negocio correcto y a enviar el informe al inbox correcto.',
          'Cuando el informe esta listo, es mas facil compartirlo, discutirlo y usarlo como punto de partida para decisiones practicas de IA.'
        ]
      },
      faqLabel: 'FAQ',
      faqs: [
        {
          question: 'Por que piden un correo de empresa?',
          answer:
            'El correo laboral nos ayuda a identificar la empresa correcta y nos da el inbox verificado donde debe llegar el informe final.'
        },
        {
          question: 'Y si nuestro sitio principal usa otro dominio?',
          answer:
            'Usa el campo opcional de sitio web. Seguiremos usando el correo corporativo para la entrega, pero revisaremos el sitio que indiques.'
        },
        {
          question: 'Vere el informe completo al instante en esta pagina?',
          answer:
            'No. Preparamos el informe en segundo plano y lo enviamos por email cuando este listo.'
        }
      ]
    },
    leadMagnet: {
      label: 'AI Readiness',
      title: 'Comprueba AI Readiness con tu correo corporativo.',
      description:
        'Introduce un correo de empresa y revisaremos el negocio detras de el, prepararemos un informe personalizado y lo enviaremos a ese inbox cuando este listo.',
      valueCards: [
        {
          title: 'Revision especifica para la empresa',
          description: 'Analizamos el negocio publico detras del correo corporativo que envias.'
        },
        {
          title: 'Preparado en segundo plano',
          description:
            'Tu informe se genera despues del envio y se entrega por email cuando esta listo.'
        },
        {
          title: 'Siguiente paso mas claro',
          description: 'Usa el informe para decidir donde debe ayudar primero la IA en el negocio.'
        }
      ],
      afterSubmitLabel: 'Despues de enviar',
      afterSubmitSteps: [
        'Relacionamos tu correo corporativo con el dominio correcto.',
        'Preparamos la revision de AI Readiness en segundo plano.',
        'Enviamos el informe final a tu inbox verificado.'
      ],
      form: {
        eyebrow: 'Correo corporativo verificado',
        title: 'Comprueba AI Readiness para tu empresa',
        description:
          'Introduce tu nombre, correo corporativo y rol. Revisaremos el dominio correspondiente y enviaremos el informe final cuando este listo.',
        modalTitle: 'Comprueba AI Readiness con un correo corporativo',
        modalDescription:
          'Introduce el inbox de empresa ligado al negocio. Prepararemos el informe en segundo plano y lo enviaremos cuando este listo.',
        fullName: 'Nombre completo',
        fullNamePlaceholder: 'Jordan Avery',
        role: 'Rol o profesion en la empresa',
        rolePlaceholder: 'Gerente de operaciones',
        companyEmail: 'Correo corporativo',
        companyEmailPlaceholder: 'equipo@tuempresa.com',
        companyDomain: 'Dominio de la empresa',
        companyDomainBadge: 'Dominio de la empresa: {{domain}}',
        useCompanyInbox: 'Usa un correo corporativo',
        freeMailHint: 'Los correos gratuitos se bloquean para revisar la empresa correcta.',
        websiteOverride: 'Sitio web alternativo',
        optional: 'Opcional',
        websitePlaceholder: 'https://tusitioprincipal.com',
        websiteHint:
          'Usalo solo si el sitio principal de tu empresa vive en un dominio distinto al del correo.',
        deliveryCheckbox:
          'Confirmo que este es mi correo corporativo y quiero recibir aqui la evaluacion de AI Readiness.',
        marketingCheckbox:
          'Acepto recibir la evaluacion de AI Readiness y comunicaciones relacionadas. Puedo darme de baja en cualquier momento.',
        modalConsentCheckbox:
          'Verifico que este es el correo correcto de mi empresa y acepto recibir aqui el informe de AI Readiness y el seguimiento relacionado.',
        backgroundHint:
          'Preparamos el informe en segundo plano y lo enviamos al inbox corporativo verificado cuando este listo.',
        preparingStatusLabel: 'Preparando ahora',
        preparingStatusDescription:
          'Estamos relacionando el dominio de la empresa, revisando la huella publica y construyendo el informe ahora.',
        submit: 'Comprobar AI Readiness',
        submitting: 'Preparando tu informe',
        successLabel: 'Preparando',
        successTitle: 'Tu informe de AI Readiness va en camino',
        successDescription:
          'Estamos preparando el informe de AI Readiness para {{businessName}} revisando {{domain}}. Cuando este listo, lo enviaremos a {{email}}.',
        checkAnother: 'Comprobar otra empresa',
        enterName: 'Introduce tu nombre completo.',
        enterRole: 'Introduce tu rol o profesion en la empresa.',
        enterEmail: 'Introduce tu correo corporativo.',
        enterValidEmail: 'Introduce un correo corporativo valido.',
        restrictedDomain: 'Usa tu correo corporativo para que podamos evaluar el dominio correcto.',
        confirmDelivery: 'Confirma tu correo empresarial antes de continuar.',
        consentRequired: 'Se requiere consentimiento antes de enviar la evaluacion.',
        submitError: 'No se pudo generar la evaluacion de AI readiness.'
      }
    },
    support: {
      eyebrow: 'Soporte',
      title: 'Habla con el equipo de Losi.',
      description:
        'Necesitas ayuda con tu cuenta, facturacion o configuracion? Envia los detalles una vez y lo moveremos a la persona correcta.',
      includeTitle: 'Que incluir',
      includeItems: [
        'Un asunto claro para enrutar la solicitud correctamente',
        'El correo ligado a tu cuenta de Losi si el problema es especifico de la cuenta',
        'Suficiente detalle para que entendamos que ocurrio y que esperabas'
      ],
      fastTitle: 'Necesitas ayuda rapido?',
      fastDescription:
        'Si tu mensaje es sobre una pagina o flujo especifico, usa los parametros de asunto y mensaje para rellenar este formulario desde otra parte del producto.',
      name: 'Nombre',
      email: 'Correo',
      subject: 'Asunto',
      message: 'Mensaje',
      namePlaceholder: 'Jordan Avery',
      emailPlaceholder: 'tu@empresa.com',
      subjectPlaceholder: 'En que podemos ayudar?',
      messagePlaceholder: 'Cuéntanos que ocurrio y que necesitas.'
    },
    canada: {
      eyebrow: 'Hecho en Canada',
      title: 'Valores canadienses, ambicion moderna en IA.',
      description:
        'Losi refleja una forma canadiense de construir tecnologia: practica, inclusiva, consciente de la privacidad y pensada para personas y empresas reales.',
      practiceLabel: 'Que significa en la practica',
      practiceItems: [
        'Una historia de producto que valora la claridad por encima del hype',
        'Soporte de idiomas inclusivo para una gran variedad de comunidades',
        'Mas atencion a confianza, privacidad y crecimiento responsable'
      ],
      principles: {
        eyebrow: 'Principios',
        title: 'La historia del producto se forma con algo mas que rendimiento.',
        description:
          'Ser canadiense no es una insignia al final. Influye en como Losi piensa sobre utilidad, lenguaje, confianza y valor a largo plazo.',
        cards: [
          {
            title: 'Hecho con una mirada canadiense',
            description:
              'Losi se posiciona alrededor de utilidad practica, confianza y un enfoque mas aterrizado para adoptar IA.'
          },
          {
            title: 'Amplitud linguistica y cultural',
            description:
              'El producto esta pensado para equipos y comunidades diversas, con soporte para muchos idiomas y casos de uso.'
          },
          {
            title: 'Privacidad desde el diseño',
            description:
              'El respeto por datos, control y decisiones responsables forma parte de como se presenta y construye la plataforma.'
          },
          {
            title: 'Direccion de producto reflexiva',
            description:
              'Nos enfocamos en flujos utiles y duraderos en lugar de gimmicks de IA que generan ruido sin valor real.'
          }
        ]
      },
      language: {
        eyebrow: 'Soporte de idiomas',
        title: 'Una vision mas amplia de para quien debe construirse la IA.',
        description:
          'Losi busca apoyar una gama mas amplia de idiomas y comunidades, incluyendo atencion a la representacion de lenguas indigenas.',
        label: 'Reconocimiento de lenguas indigenas',
        panelTitle: 'Preserva mas que utilidad. Preserva presencia cultural.',
        panelDescription:
          'Un soporte linguistico amplio ayuda a que la IA sea mas representativa, mas util y mas respetuosa con las comunidades a las que sirve.'
      }
    }
  },
  fr: englishMarketingCopy,
  de: englishMarketingCopy,
  pt: englishMarketingCopy,
  ar: englishMarketingCopy,
  ja: englishMarketingCopy,
  ko: englishMarketingCopy,
  zh: englishMarketingCopy
}

export function resolveMarketingLocale(language?: string): MarketingLocale {
  if (!language) {
    return 'en'
  }

  const normalizedLanguage = language.toLowerCase()

  if (normalizedLanguage.startsWith('es')) {
    return 'es'
  }

  if (normalizedLanguage.startsWith('fr')) {
    return 'fr'
  }

  if (normalizedLanguage.startsWith('de')) {
    return 'de'
  }

  if (normalizedLanguage.startsWith('pt')) {
    return 'pt'
  }

  if (normalizedLanguage.startsWith('ar')) {
    return 'ar'
  }

  if (normalizedLanguage.startsWith('ja')) {
    return 'ja'
  }

  if (normalizedLanguage.startsWith('ko')) {
    return 'ko'
  }

  if (normalizedLanguage.startsWith('zh')) {
    return 'zh'
  }

  return 'en'
}

export function getMarketingCopy(language?: string): MarketingCopy {
  const locale = resolveMarketingLocale(language)

  return marketingCopyByLocale[locale]
}
